import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import { request } from "@/utils/api";

// Create entity adapter
const codRemittanceAdapter = createEntityAdapter({
    selectId: (remittance) => remittance._id, // Assuming each remittance has an _id field
    sortComparer: (a, b) => new Date(b.createdAt) - new Date(a.createdAt) // Sort by date descending
});

// Async thunk for fetching COD remittances
export const fetchCodRemittance = createAsyncThunk(
    "codRemittance/fetch",
    async (_, { rejectWithValue }) => {
        try {
            // Call the API to fetch COD remittances
            const response = await request({
                endpoint: "/user/codRemittance",
                method: "GET",
            });

            console.log("Fetch COD Remittances Response:", response);

            // Check if the fetch operation was successful
            if (!response.success || !Array.isArray(response.data?.remittance)) {
                return rejectWithValue(response.error.message || "Failed to fetch COD remittances.");
            }

            // Return the fetched COD remittances data
            return response.data;
        } catch ({ error }) {
            console.error("Error fetching COD remittances:", error.message);
            return rejectWithValue(error.message || "An unexpected error occurred while fetching COD remittances.");
        }
    }
);

const initialState = codRemittanceAdapter.getInitialState({
    totalDue: null,
    totalRemittance: null,
    lastRemittance: null,
    loading: false,
    error: null,
});

const codRemittanceSlice = createSlice({
    name: "codRemittance",
    initialState,
    reducers: {
        addCodRemittance: (state, action) => {
            codRemittanceAdapter.setAll(state, action.payload.remittance);
            state.totalDue = action.payload.totalDue;
            state.totalRemittance = action.payload.totalRemittance;
            state.lastRemittance = action.payload.lastRemittance;
        },
        clearCodRemittance: (state) => {
            codRemittanceAdapter.removeAll(state);
            state.totalDue = null;
            state.totalRemittance = null;
            state.lastRemittance = null;
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCodRemittance.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCodRemittance.fulfilled, (state, action) => {
                state.loading = false;
                codRemittanceAdapter.setAll(state, action.payload.remittance);
                state.totalDue = action.payload.totalDue;
                state.totalRemittance = action.payload.totalRemittance;
                state.lastRemittance = action.payload.lastRemittance;
            })
            .addCase(fetchCodRemittance.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// Export the adapter selectors
export const {
    selectAll: selectAllCodRemittances,
    selectById: selectCodRemittanceById,
    selectIds: selectCodRemittanceIds,
} = codRemittanceAdapter.getSelectors((state) => state.codRemittance);

export const { clearCodRemittance, addCodRemittance } = codRemittanceSlice.actions;
export default codRemittanceSlice.reducer;